import { useCallback, useRef, useState } from "react";
import { getApiBase } from "@/lib/config";

export interface StreamEvent {
  event: string;
  data: { delta?: string; name?: string; input?: unknown; output?: string; message?: string };
}

export interface UseStreamGenerateOptions {
  onEvent?: (e: StreamEvent) => void;
}

/**
 * Generic SSE streamer for the `/chat/stream` endpoint that feature pages
 * (content-factory, seller-pitch, agent-brand, market-watch, rental-report)
 * use to stream AI-generated content sections. Keeps parsing logic in one
 * place so each page only wires up its own UI state.
 */
export function useStreamGenerate(opts: UseStreamGenerateOptions = {}) {
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [text, setText] = useState("");
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setStreaming(false);
  }, []);

  const run = useCallback(async (messages: Array<{ role: string; content: string }>, extra?: Record<string, unknown>) => {
    setStreaming(true);
    setError(null);
    setText("");
    setActiveTool(null);
    const abort = new AbortController();
    abortRef.current = abort;
    const base = getApiBase();
    let accumulated = "";
    // PROP-200: track any failure (error SSE / throw / abort) so we never
    // hand a truncated stream back to the caller — a partial AI summary
    // must not be persisted as a finished result (data honesty).
    let errored = false;

    try {
      const res = await fetch(`${base}/chat/stream`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ messages, ...(extra || {}) }),
        signal: abort.signal,
        // Same reason as useChat — without credentials the BE can't
        // identify the agent's session, so any agent-scoped tool the
        // generator might call (now or future) returns NOT_AUTHENTICATED.
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let event = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() ?? "";
        for (const line of lines) {
          if (line === "" || line.startsWith(":")) continue;
          if (line.startsWith("id: ")) continue;
          if (line.startsWith("event: ")) { event = line.slice(7).trim(); continue; }
          if (!line.startsWith("data: ")) continue;
          let payload: StreamEvent["data"];
          try { payload = JSON.parse(line.slice(6)); } catch { continue; }

          opts.onEvent?.({ event, data: payload });

          if (event === "text") {
            accumulated += payload.delta || "";
            setText(accumulated);
          } else if (event === "tool_call") {
            setActiveTool(payload.name || "tool");
          } else if (event === "tool_result") {
            setActiveTool(null);
          } else if (event === "error") {
            setError(payload.message || "Stream error");
            errored = true;
          }
          event = "";
        }
      }
    } catch (e) {
      if ((e as Error).name !== "AbortError") {
        setError((e as Error).message);
      }
      // Abort or exception: accumulated text is incomplete — don't pass it
      // back as if it were a finished result.
      errored = true;
    } finally {
      abortRef.current = null;
      setStreaming(false);
      setActiveTool(null);
    }
    // On any failure return "" so callers' `if (out)` / `if (!text)` guards
    // skip persisting a half-finished AI summary as final (PROP-200).
    return errored ? "" : accumulated;
  }, [opts]);

  return { streaming, text, error, activeTool, run, cancel, setText };
}
